import React, { useCallback, useState } from "react";
import cx from "classnames";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useMutation } from "@tanstack/react-query";
import { useTranslate } from "../../hooks/useTranslate";
import BaseDialog from "../BaseDialog";
import axiosManager from "../../services/axiosManager";
import SnackbarToast from "../SnackbarToast";
import "./ContactDetailsBox.css";
import { useToast } from "../../context/toast";
import { getCountryDialCode, parseCountryDialCode } from "../../constants/countryCodes";

const ContactDetailsBox = (props) => {
    const {
        contact,
        isPrimary,
        showName,
        relationshipsOptions,
        countries,
        className,
        refetch,
        onAfterAccessGrant,
    } = props;

    const { t } = useTranslate();
    const toast = useToast();

    const [showAccessConfirm, setShowAccessConfirm] = useState(false);
    const [showAccessFinanceConfirm, setShowAccessFinanceConfirm] = useState(false);
    const [showAccessRenewalsConfirm, setShowAccessRenewalsConfirm] = useState(false);

    const fullName = `${contact.title ? `${contact.title} ` : ""}${contact.forename} ${contact.surname}`;
    const relationship = relationshipsOptions.find((it) => it.value === contact?.relationshipTypeId);
    const countryName = countries.find((it) => it.countryCode === contact.primaryAddress?.country)?.name;
    const addressLine = [contact.primaryAddress?.addressLine1, contact.primaryAddress?.city, contact.primaryAddress?.area, contact.primaryAddress?.code, countryName]
        .filter((field) => !!field)
        .join("\n");
    const formatPhone = useCallback((phone) => `${phone.countryCode ? `${getCountryDialCode(phone.countryCode)} ${phone.number}` : parseCountryDialCode(phone.number)}`, []);
    const allowFinance = (contact?.students || []).some((student) => student.allowFinance);
    const allowRenewals = (contact?.students || []).some((student) => student.allowRenewals);

    const { mutateAsync: updateContactPermissions, isPending: isUpdatingContactPermissions } = useMutation({
        mutationFn: (body) => axiosManager.put$(`/parent/api/contacts/${contact.contactId}/permissions`, body),
        onSuccess: () => toast.success(t("responsibleAdults.contactInformation.successUpdateMessage")),
    });

    const onCloseAccessConfirm = () => {
        setShowAccessConfirm(false);
    };
    const onCloseAccessFinanceConfirm = () => {
        setShowAccessFinanceConfirm(false);
    };
    const onCloseAccessRenewalsConfirm = () => {
        setShowAccessRenewalsConfirm(false);
    };
    const onGrantAccess = (additionalData) => async () => {
        const body = {
            hasParentHubAccess: contact.hasParentHubAccess,
            allowRenewals,
            allowFinance,
            ...(additionalData || {}),
        };

        try {
            await updateContactPermissions(body);

            refetch?.();
            if (additionalData.hasParentHubAccess) onAfterAccessGrant?.();
        } catch (err) {
            const errorData = err.response.data;

            if (errorData === "Account already exists") {
                toast.error(t("myDetails.accessModal.errorSameEmail"));
            } else {
                toast.error(t("common.errorMessage"));
            }
        } finally {
            setShowAccessConfirm(false);
            setShowAccessRenewalsConfirm(false);
            setShowAccessFinanceConfirm(false);
        }
    };

    const renderAccessModal = useCallback((open, onClose, title, description, isGranting, onGrantAccess) => (
        <BaseDialog open={open} onClose={onClose} title={title} className="p-4" isWide>
            <Typography variant="body2" component="div">{description}</Typography>
            <div className="w-full flex align-center gap-8 mt-8">
                <Button
                    variant="outlined"
                    className="w-full"
                    onClick={onClose}
                >
                    {t("button.cancel")}
                </Button>
                <Button
                    variant="contained"
                    className="w-full button-accent"
                    loading={isGranting}
                    onClick={onGrantAccess}
                >
                    {t("myDetails.accessModal.grantAccess")}
                </Button>
            </div>
        </BaseDialog>
    ), [t]);

    return (
        <>
            <div className={cx("contact-details-box", className)}>
                {showName && (
                    <div className="contact-details-box__row">
                        <span className="contact-details-box__title">{t("myDetails.name")}</span>
                        <span className="contact-details-box__value">{fullName}</span>
                    </div>
                )}
                {isPrimary && (
                    <div className="contact-details-box__row">
                        <span className="contact-details-box__title">{t("myDetails.relationshipToStudent")}</span>
                        <span className="contact-details-box__value">{relationship?.label || "-"}</span>
                    </div>
                )}
                <div className="contact-details-box__row">
                    <span className="contact-details-box__title">{t("myDetails.email")}</span>
                    <span className="contact-details-box__value">{contact?.emailAddress}</span>
                </div>
                <div className="contact-details-box__row">
                    <span className="contact-details-box__title">{t("myDetails.tel")}</span>
                    <span className="contact-details-box__value">
                        {contact?.primaryPhone ? formatPhone(contact?.primaryPhone) : "-"}
                    </span>
                </div>
                {!isPrimary && (
                    <div className="contact-details-box__row">
                        <span className="contact-details-box__title">{t("myDetails.contactType")}</span>
                        <span className="contact-details-box__value">{contact?.contactType || "-"}</span>
                    </div>
                )}
                {(!isPrimary && !showName) && (
                    <div className="contact-details-box__row">
                        <span className="contact-details-box__title">{t("myDetails.accessToggleTo")}</span>
                        <div className="contact-details-box__value flex flex-column mr-2">
                            <FormControlLabel
                                key={contact.contactId}
                                disabled={contact.hasParentHubAccess}
                                control={(
                                    <Switch
                                        checked={contact.hasParentHubAccess ? true : showAccessConfirm}
                                        onChange={(_, checked) => {
                                            setShowAccessConfirm(checked);
                                        }}
                                    />
                                )}
                                className="contact-details-box__toggle"
                                label={t("myDetails.parentHub")}
                                labelPlacement="start"
                            />
                            {/* <FormControlLabel */}
                            {/*    disabled={!contact.hasParentHubAccess || allowFinance} */}
                            {/*    control={( */}
                            {/*        <Switch */}
                            {/*            checked={allowFinance} */}
                            {/*            onChange={(_, checked) => { */}
                            {/*                setShowAccessFinanceConfirm(checked); */}
                            {/*            }} */}
                            {/*        /> */}
                            {/*    )} */}
                            {/*    className="contact-details-box__toggle" */}
                            {/*    label={t("myDetails.finance")} */}
                            {/*    labelPlacement="start" */}
                            {/* /> */}
                            <FormControlLabel
                                disabled={!contact.hasParentHubAccess || allowRenewals}
                                control={(
                                    <Switch
                                        checked={allowRenewals}
                                        onChange={(_, checked) => {
                                            setShowAccessRenewalsConfirm(checked);
                                        }}
                                    />
                                )}
                                className="contact-details-box__toggle"
                                label={t("myDetails.renewals")}
                                labelPlacement="start"
                            />
                        </div>
                    </div>
                )}
                {/* <div className="contact-details-box__row">
                <span className="contact-details-box__title">{t("myDetails.dateOfBirth")}</span>
                <span
                    className="contact-details-box__value"
                >
                    {contact?.dateOfBirth ? formatTo(contact?.dateOfBirth, "dd/MM/yyyy", "yyyy-MM-dd") : "-"}
                </span>
            </div> */}
                <div className="divider" />
                <div className="contact-details-box__column">
                    <span className="contact-details-box__title">{t("myDetails.address")}</span>
                    <span
                        className="contact-details-box__value contact-details-box__value-full"
                    >
                        {addressLine || "-"}
                    </span>
                </div>
            </div>
            {renderAccessModal(showAccessConfirm, onCloseAccessConfirm, t("myDetails.accessModal.title"), t("myDetails.accessModal.description"), isUpdatingContactPermissions, onGrantAccess({ hasParentHubAccess: true }))}
            {renderAccessModal(showAccessFinanceConfirm, onCloseAccessFinanceConfirm, t("myDetails.accessFinanceModal.title"), t("myDetails.accessFinanceModal.description"), isUpdatingContactPermissions, onGrantAccess({ allowFinance: true }))}
            {renderAccessModal(showAccessRenewalsConfirm, onCloseAccessRenewalsConfirm, t("myDetails.accessRenewalsModal.title"), t("myDetails.accessRenewalsModal.description"), isUpdatingContactPermissions, onGrantAccess({ allowRenewals: true }))}
        </>
    );
};

export default ContactDetailsBox;
